import { SCHOOLS } from '@nine/shared';
import { bell, brush, drop, flower, sabre, scroll, star, sword, taiji } from '../kit';
import { C, type Hex, mix, type Pix } from '../pix';
import { defineIconKind, registerIcons } from '../store';

/**
 * Schools (สำนัก): a crest in the school's colour with a gold rim and its emblem. Badge ranks
 * (ป้ายเหล็ก ป้ายสำริด ป้ายเงิน ป้ายทอง): a hanging tablet in the badge's metal, one to four studs.
 */

export const SCHOOL_COLORS: Readonly<Record<string, Hex>> = {
  'mist-peak': '#4a7ac8',
  'bell-stone': '#e8a030',
  'lantern-road': '#c8302a',
  'dim-moon': '#a050c8',
  'pine-ink': '#2a9a8a',
  'cinnabar': '#8a1a22',
};

/** A crest: a shield-like banner with a gold rim. */
function crest(p: Pix, col: Hex): void {
  p.poly(
    [
      [4, 3],
      [28, 3],
      [28, 18],
      [16, 29],
      [4, 18],
    ],
    C.gold,
  );
  p.poly(
    [
      [6, 5],
      [26, 5],
      [26, 17.5],
      [16, 26.5],
      [6, 17.5],
    ],
    col,
  );
  p.rect(6, 5, 20, 2, mix(col, C.white, 0.3));
}

defineIconKind('school', {
  label: 'สำนัก',
  ids: () => Object.keys(SCHOOLS),
  name: (id) => SCHOOLS[id]?.name ?? id,
  tint: (id) => SCHOOL_COLORS[id] ?? C.gold,
  plate: 'none',
  fallback: (id) => ({ draw: (p) => crest(p, SCHOOL_COLORS[id] ?? C.steelMid) }),
});

registerIcons('school', {
  'mist-peak': (p) => {
    crest(p, SCHOOL_COLORS['mist-peak']!);
    taiji(p, 16, 14, 6);
    sword(p, 9, 24, 23, 9, { hilt: C.gold, w: 1.6 });
  },
  'bell-stone': (p) => {
    crest(p, SCHOOL_COLORS['bell-stone']!);
    for (let k = 0; k < 12; k++) {
      const a = (k * Math.PI) / 6;
      p.rect(Math.round(16 + Math.cos(a) * 7) - 1, Math.round(15 + Math.sin(a) * 7) - 1, 2, 2, '#7a3a1a');
    }
    bell(p, 16, 10, 9, C.goldLight, { w: 8 });
  },
  'lantern-road': (p) => {
    crest(p, SCHOOL_COLORS['lantern-road']!);
    sabre(p, 9, 23, 24, 7, { hilt: C.gold, w: 3, curve: -1.6 });
    star(p, 11, 10, 2, C.goldLight);
  },
  'dim-moon': (p) => {
    crest(p, SCHOOL_COLORS['dim-moon']!);
    flower(p, 16, 14, 7, '#ffb0d8', C.gold);
    flower(p, 22, 21, 3, '#ff8ad0', C.gold);
  },
  'pine-ink': (p) => {
    crest(p, SCHOOL_COLORS['pine-ink']!);
    scroll(p, 11, 8, 10, 13);
    brush(p, 23, 7, 12, 22, C.ink);
  },
  'cinnabar': (p) => {
    crest(p, SCHOOL_COLORS['cinnabar']!);
    sabre(p, 9, 23, 24, 7, { blade: '#ffb0a8', hilt: '#2a1a1a', w: 3.5 });
    drop(p, 21, 19, 2.4, '#ff4a4a');
  },
});
